import { useSelector } from 'react-redux';

import { germanyCountryCode } from '@components/country-code/constants';
import { CountryData } from '@components/country-code/types';
import Label from '@components/label/Label';
import { FEATURE_FLAGS } from '@constants/feature';
import { duplicatePhoneNumberErrorMessage } from '@constants/generic';
import { invalidCharInNameRegex } from '@constants/regex';
import CustomTermTable from '@features/group-management/components/custom-term/CustomTermTable';
import { PaymentCollectionType } from '@features/group-management/constants';
import { isValidPhoneNumber } from '@helpers/generic';
import {
  AddMemberFormDataType,
  MemberListType,
  PaymentCollectionArg,
  TermsList,
  ValidationError
} from '@types';
import {
  getAmountErrorMessage,
  getNameErrorMessage,
  isMinLengthSatisfied,
  sanitizeNumberInput
} from '@utils/generic';
import { isInvalidEmail } from '@utils/validations';

import AmountInput from '../amount-input/AmountInput';
import AmountInputTooltip from '../amount-input-tooltip/AmountInputTooltip';
import CountryCode from '../country-code/CountryCode';
import FeeConfigInput from '../fee-config-input/FeeConfigInput';
import Input from '../input/Input';
import MemberDueDate from './MemberDueDate';
import MemberEndDate from './MemberEndDate';
import MemberStartDate from './MemberStartDate';

interface MemberBasicInfoProps {
  error: ValidationError;
  setError: (error: ValidationError) => void;
  memberNameMaxLength: number;
  memberNameMinLength: number;
  isFeeCategoryEnabled: boolean;
  isImmediatePayment: boolean;
  isCustomTerm: boolean;
  groupMemberMaxAmount: string;
  groupMemberMinAmount: string;
  selectedmember?: MemberListType | null;
  paymentCollectionType: PaymentCollectionType | '';
  groupFrequency?: number;
  groupStartDate?: string;
  groupEndDate?: string;
  isEmailEnabled: boolean;
  formData: AddMemberFormDataType;
  handleFormDataChange: (
    field: keyof AddMemberFormDataType,
    value: string
  ) => void;
  supported_phone_number_countries: any;
  setFormData: React.Dispatch<React.SetStateAction<AddMemberFormDataType>>;
  setIsCalendarVisible: (isCalendarVisible: boolean) => void;
}

const MemberBasicInfo = ({
  error,
  memberNameMaxLength,
  memberNameMinLength,
  isFeeCategoryEnabled,
  isImmediatePayment,
  isCustomTerm,
  groupMemberMaxAmount,
  groupMemberMinAmount,
  selectedmember,
  paymentCollectionType,
  groupFrequency,
  groupStartDate,
  groupEndDate,
  isEmailEnabled,
  formData,
  setError,
  handleFormDataChange,
  supported_phone_number_countries,
  setFormData,
  setIsCalendarVisible
}: MemberBasicInfoProps) => {
  const { orgFeatureFlags } = useSelector((state: any) => state.user);
  const {
    name,
    primaryCountryCode,
    primaryNumber,
    altCountryCode,
    altNumber,
    amount,
    feeCategories,
    dueDate,
    startDate,
    email,
    endDate,
    totalFee
  } = formData;

  const formattedFeeCategories = feeCategories.map(({ label, value }) => ({
    category: label,
    amount: value
  }));

  const onMemberNameChange = (value: string) => {
    setError({ ...error, name: '' });
    handleFormDataChange('name', value);
  };

  const onMemberNameBlur = (value: string) => {
    if (!isMinLengthSatisfied(value))
      setError({
        ...error,
        name: getNameErrorMessage(memberNameMinLength)
      });
  };

  const getCountryId = (countryCode: string) =>
    (supported_phone_number_countries || []).find(
      (countryData: CountryData) => `${countryData.dial_code}` === countryCode
    )?.code;

  const onPrimaryCountryCodeChange = (value: string) => {
    const numberToValidate =
      value !== germanyCountryCode && formData.primaryNumber.length > 10
        ? formData.primaryNumber.slice(0, 10)
        : formData.primaryNumber;
    if (
      numberToValidate &&
      !isValidPhoneNumber(numberToValidate, getCountryId(value))
    ) {
      setError({ ...error, primaryNum: 'Invalid phone number' });
    } else {
      setError({ ...error, primaryNum: '' });
    }
    if (numberToValidate !== formData.primaryNumber) {
      handleFormDataChange('primaryNumber', numberToValidate);
    }
    handleFormDataChange('primaryCountryCode', value);
  };

  const onPrimaryNumberChange = (value: string) => {
    if (formData.primaryNumber === formData.altNumber) {
      setError({ ...error, altNum: '', primaryNum: '' });
    } else {
      setError({ ...error, primaryNum: '' });
    }
    const maxLength =
      formData.primaryCountryCode === germanyCountryCode ? 11 : 10;
    if (value.length <= maxLength) {
      handleFormDataChange('primaryNumber', value);
    }
  };

  const onPrimaryNumberBlur = (value: string) => {
    if (!value) return;
    let errorMessage = '';
    if (!isValidPhoneNumber(value, getCountryId(formData.primaryCountryCode)))
      errorMessage = 'Invalid phone number';
    if (errorMessage !== '') setError({ ...error, primaryNum: errorMessage });
    if (
      value &&
      `${formData.primaryCountryCode}${value}` ===
        `${formData.altCountryCode}${formData.altNumber}`
    )
      setError({ ...error, altNum: duplicatePhoneNumberErrorMessage });
  };

  const onAltCountryCodeChange = (value: string) => {
    const numberToValidate =
      value !== germanyCountryCode && formData.altNumber.length > 10
        ? formData.altNumber.slice(0, 10)
        : formData.altNumber;
    if (
      numberToValidate &&
      !isValidPhoneNumber(numberToValidate, getCountryId(value))
    ) {
      setError({ ...error, altNum: 'Invalid phone number' });
    } else {
      setError({ ...error, altNum: '' });
    }
    if (numberToValidate !== formData.altNumber) {
      handleFormDataChange('altNumber', numberToValidate);
    }
    handleFormDataChange('altCountryCode', value);
  };

  const onAltNumberChange = (value: string) => {
    if (formData.altNumber === formData.primaryNumber) {
      setError({ ...error, primaryNum: '', altNum: '' });
    } else {
      setError({ ...error, altNum: '' });
    }
    const maxLength = formData.altCountryCode === germanyCountryCode ? 11 : 10;
    if (value.length <= maxLength) {
      handleFormDataChange('altNumber', value);
    }
  };

  const onAltNumberBlur = (value: string) => {
    if (!value) return;
    let errorMessage = '';
    if (!isValidPhoneNumber(value, getCountryId(formData.altCountryCode)))
      errorMessage = 'Invalid phone number';
    else if (
      value &&
      `${formData.primaryCountryCode}${formData.primaryNumber}` ===
        `${formData.altCountryCode}${value}`
    )
      errorMessage = duplicatePhoneNumberErrorMessage;
    if (errorMessage !== '') setError({ ...error, altNum: errorMessage });
  };

  const onAmountChange = (value: string) => {
    setError({ ...error, amount: '' });
    const sanitizedAmount = sanitizeNumberInput(value);
    if (Number(sanitizedAmount) > Number(groupMemberMaxAmount)) {
      setError({
        ...error,
        amount: `Amount cannot be greater than ${groupMemberMaxAmount}`
      });
    }
    handleFormDataChange('amount', sanitizedAmount);
  };

  const onTotalFeeChange = (value: string) => {
    setError({ ...error, totalFee: '' });
    handleFormDataChange('totalFee', value);
    if (
      Number(value) !== 0 &&
      value !== '0' &&
      Number(value) > Number(groupMemberMaxAmount)
    ) {
      setError({
        ...error,
        totalFee: `Amount cannot be greater than ${groupMemberMaxAmount}`
      });
    }
  };

  const onAmountBlur = (value: string) => {
    if (
      value !== '0' &&
      Number(value) !== 0 &&
      Number(value) < Number(groupMemberMinAmount)
    ) {
      setError({
        ...error,
        amount: getAmountErrorMessage(Number(groupMemberMinAmount))
      });
    }
  };

  const onTotalFeeBlur = (value: string) => {
    if (Number(value) !== 0 && Number(value) < Number(groupMemberMinAmount)) {
      setError({
        ...error,
        totalFee: `Amount cannot be less than ${groupMemberMinAmount}`
      });
    }
  };

  const onCategoryAmountChange = (index: number, value: string) => {
    const updatedCategories = [...formData.feeCategories];
    updatedCategories[index].value = sanitizeNumberInput(value);
    updatedCategories[index].isManuallyUpdated = true;
    const totalAmount = updatedCategories.reduce(
      (sum, category) => sum + (Number(category.value) || 0),
      0
    );
    setFormData({
      ...formData,
      amount: totalAmount.toString(),
      feeCategories: updatedCategories
    });
  };

  const onDueDateChange = (data: PaymentCollectionArg) => {
    setFormData({
      ...formData,
      dueDate: {
        day: data.date as string,
        interval: data.interval,
        paymentCollectionType: data.type
      }
    });
    setIsCalendarVisible(false);
  };

  const onTermsListChange = (termsList: TermsList[]) => {
    setFormData((prevFormData: AddMemberFormDataType) => ({
      ...prevFormData,
      termsList
    }));
  };

  const onStartDateChange = (date: string) => {
    handleFormDataChange('startDate', date);
  };

  const getEndOfTermDate = () => {
    if (!isCustomTerm || !formData.termsList || formData.termsList.length === 0)
      return undefined;

    const finalTerm = formData.termsList[formData.termsList.length - 1];
    return new Date(finalTerm.termEndDate);
  };

  const onEndDateChange = (date: string) => {
    setError({ ...error, endDate: '' });
    handleFormDataChange('endDate', date);
    if (date && new Date(date) < new Date(startDate)) {
      setError({
        ...error,
        endDate: 'End date cannot be before start date'
      });
    }

    if (date && groupEndDate && new Date(date) > new Date(groupEndDate)) {
      setError({
        ...error,
        endDate: 'End date cannot be after group end date'
      });
    }
  };

  const onMemberEmailChange = (value: string) => {
    setError({ ...error, email: '' });
    handleFormDataChange('email', value);
  };

  const onEmailBlur = (value: string) => {
    if (value && isInvalidEmail(value)) {
      setError({ ...error, email: 'Invalid email' });
    }
  };

  return (
    <div className="no-scrollbar max-h-[400px] overflow-y-auto">
      <div className="grid w-full grid-cols-2 gap-4 py-3">
        <div className="gap-4">
          <Label label="Name" isRequired />
          <Input
            value={name}
            handleInputChange={onMemberNameChange}
            inputRegex={invalidCharInNameRegex}
            type="text"
            placeholder="Name"
            maxLength={memberNameMaxLength}
            minLength={memberNameMinLength}
            error={!!error.name}
            errorMessage={error.name}
            handleInputBlur={onMemberNameBlur}
          />
        </div>

        <div className="gap-4">
          <Label label="Primary Number" isRequired />
          <div className="flex gap-3">
            <CountryCode
              hasSelection
              countryCode={primaryCountryCode}
              setCountryCode={onPrimaryCountryCodeChange}
            />
            <Input
              className="w-full"
              value={primaryNumber}
              handleInputChange={onPrimaryNumberChange}
              placeholder="Enter phone number"
              type="phone"
              error={!!error.primaryNum}
              errorMessage={error.primaryNum}
              handleInputBlur={onPrimaryNumberBlur}
            />
          </div>
        </div>

        <div className="gap-4">
          <Label label="Alternate Number" />
          <div className="flex gap-3">
            <CountryCode
              hasSelection
              countryCode={altCountryCode}
              setCountryCode={onAltCountryCodeChange}
            />
            <Input
              className="w-full"
              value={altNumber}
              handleInputChange={onAltNumberChange}
              placeholder="Enter phone number"
              type="phone"
              error={!!error.altNum}
              errorMessage={error.altNum}
              handleInputBlur={onAltNumberBlur}
              errorStyle={
                error.altNum === duplicatePhoneNumberErrorMessage
                  ? '!-bottom-10'
                  : ''
              }
            />
          </div>
        </div>
        <div>
          <div className="flex flex-col">
            <div className="mb-4 text-sm font-semibold text-grey">Amount</div>
            <AmountInput
              value={amount}
              placeholder="Enter Amount"
              onAmountChange={onAmountChange}
              handleInputBlur={onAmountBlur}
              error={isCustomTerm ? false : !!error.amount}
              disabled={isFeeCategoryEnabled || isCustomTerm}
            />
            {!isCustomTerm && (
              <AmountInputTooltip
                maxAmount={groupMemberMaxAmount}
                minAmount={groupMemberMinAmount}
                type="hasSubtext"
              />
            )}
          </div>
        </div>

        {!isImmediatePayment && (
          <MemberDueDate
            dueDate={dueDate}
            memberPaymentCollectionType={
              selectedmember?.paymentCollectionType as PaymentCollectionType
            }
            memberInterval={selectedmember?.interval as string}
            memberDueDate={selectedmember?.dueDate as string}
            paymentCollectionType={paymentCollectionType}
            groupFrequency={groupFrequency as number}
            onDueDateChange={onDueDateChange}
            isCustomTerm={isCustomTerm}
          />
        )}
        {/* Start date */}
        {paymentCollectionType !== PaymentCollectionType.ONCE && (
          <MemberStartDate
            startDate={startDate || groupStartDate || ''}
            onStartDateChange={onStartDateChange}
            maxLimitDate={getEndOfTermDate()}
            isDisabled={isCustomTerm}
          />
        )}

        {paymentCollectionType !== PaymentCollectionType.ONCE &&
          orgFeatureFlags?.includes(FEATURE_FLAGS.MEMBER_END_DATE) && (
            <MemberEndDate
              endDate={endDate}
              onEndDateChange={onEndDateChange}
              isDisabled={isCustomTerm}
              error={!!error.endDate}
              errorMessage={error.endDate}
            />
          )}

        {orgFeatureFlags &&
          orgFeatureFlags.includes(FEATURE_FLAGS.LEAD_SQUARED) && (
            <div>
              <div className="flex flex-col">
                <div className="mb-4 text-sm font-semibold text-grey">
                  Course fee
                </div>
                <AmountInput
                  value={totalFee}
                  placeholder="Enter course fee"
                  onAmountChange={onTotalFeeChange}
                  handleInputBlur={onTotalFeeBlur}
                  error={!!error.totalFee}
                  errorMessage={error.totalFee}
                />
              </div>
            </div>
          )}

        {isEmailEnabled && (
          <div className="gap-4">
            <Label label="Email" />
            <Input
              value={email}
              handleInputChange={onMemberEmailChange}
              type="text"
              placeholder="Email"
              error={!!error.email}
              errorMessage={error.email}
              handleInputBlur={onEmailBlur}
            />
          </div>
        )}

        {isFeeCategoryEnabled && !isCustomTerm && (
          <div className="col-span-2">
            <FeeConfigInput
              feeCategories={formattedFeeCategories}
              onCategoryAmountChange={({ index, value }) => {
                onCategoryAmountChange(index, value);
              }}
              memberActionType="add"
            />
          </div>
        )}

        {isCustomTerm && formData.termsList?.length > 0 && (
          <div className="col-span-2">
            <CustomTermTable
              termsList={formData.termsList}
              setTermsList={onTermsListChange}
              isFeeCategoryEnabled={isFeeCategoryEnabled}
              feeCategories={feeCategories}
              onAmountChange={(totalAmount) =>
                handleFormDataChange('amount', totalAmount)
              }
              isMember
              memberFlow="add"
              setError={setError}
              error={error}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default MemberBasicInfo;
